/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useClassOfServiceData.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Hook for fetching Class of Service data and options.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import {
  fetchClassOfServiceById,
  fetchFeatureCodeCategories,
  fetchDialRuleRestrictions,
  fetchArsProfiles,
} from '../api/classOfServicesApi';
import { FormState, FeatureCodeCategory, DialRuleRestriction, ArsProfile } from '../types';
import { getDefaultFormState } from '../utils/formDefaults';

// Accepts plain integers OR UUID strings (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)
const ID_PATTERN = /^(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

/**
 * Hook for fetching Class of Service data
 */
export function useClassOfServiceData(id: string | undefined, duplicateId: string | null) {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<FormState | null>(null);
  const [featureCodeCategories, setFeatureCodeCategories] = useState<FeatureCodeCategory[]>([]);
  const [dialRuleRestrictions, setDialRuleRestrictions] = useState<DialRuleRestriction[]>([]);
  const [arsProfiles, setArsProfiles] = useState<ArsProfile[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const { showError } = useNotifications();
  const { t } = useI18n();
  const showErrorRef = useRef(showError);
  const lastLoadedIdRef = useRef<string | null>(null);
  const lastLocationKeyRef = useRef<string>('');
  const hasLoadedOptionsRef = useRef(false);
  const tRef = useRef(t);

  useEffect(() => {
    showErrorRef.current = showError;
    tRef.current = t;
  }, [showError, t]);

  const loadClassOfService = useCallback(
    async (loadId: string, duplicate: boolean = false) => {
      setIsLoading(true);
      try {
        const payload = await fetchClassOfServiceById(loadId, tRef.current);

        const baseName = (payload.name ?? '').trim();
        const copySuffix = tRef.current('common.copySuffix', 'Copy');
        const resolvedName =
          !duplicate ? baseName : `${baseName} ${copySuffix}`.trim();

        const normalised: FormState = {
          name: resolvedName,
          description: (payload.description ?? '').trim(),
          featureCodeCosId: payload.featureCodeCosId ?? null,
          dialRuleRestrictionId: payload.dialRuleRestrictionId ?? null,
          arsId: payload.arsId ?? null,
          isMain: payload.isMain ?? false,
          enabled: payload.enabled ?? true,
          failoverDestinationModule: payload.failoverDestinationModule || 'terminate',
          failoverDestinationData: payload.failoverDestinationData ?? null,
        };

        setFormData(normalised);
      } catch (error) {
        showErrorRef.current(tRef.current('classOfServices.messages.loadError'));
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    const loadId = duplicateId || id;
    const savedDataKey = 'classOfServices:saveAndNewData';
    const locationKey = location.key || location.pathname + location.search;

    if (!loadId || loadId === 'new') {
      // Check if we need to reset:
      // 1. ID changed
      // 2. Location key changed (React Router navigation)
      // 3. sessionStorage timestamp indicates reset needed
      const resetTimestamp = sessionStorage.getItem(savedDataKey + ':reset');
      const lastResetTimestamp = lastLoadedIdRef.current === 'new' ?
        sessionStorage.getItem(savedDataKey + ':lastReset') : null;

      const shouldReset = lastLoadedIdRef.current !== 'new' ||
        lastLocationKeyRef.current !== locationKey ||
        (lastLoadedIdRef.current === 'new' && loadId === 'new' &&
          resetTimestamp && resetTimestamp !== lastResetTimestamp);

      if (shouldReset) {
        lastLoadedIdRef.current = 'new';
        lastLocationKeyRef.current = locationKey;

        // Clear sessionStorage for "Save and New" functionality
        try {
          sessionStorage.removeItem(savedDataKey);
          if (resetTimestamp) {
            sessionStorage.setItem(savedDataKey + ':lastReset', resetTimestamp);
            sessionStorage.removeItem(savedDataKey + ':reset');
          }
        } catch (error) {
          // Ignore errors when clearing
        }

        if (!duplicateId) {
          // Reset form to default values
          setFormData(getDefaultFormState());
        }
      }
      return;
    }

    if (!ID_PATTERN.test(loadId)) {
      showErrorRef.current(tRef.current('classOfServices.messages.loadError'));
      lastLoadedIdRef.current = null;
      return;
    }

    if (lastLoadedIdRef.current === loadId && lastLocationKeyRef.current === locationKey) {
      return;
    }

    lastLoadedIdRef.current = loadId;
    lastLocationKeyRef.current = locationKey;
    loadClassOfService(loadId, Boolean(duplicateId)).catch(() => {
      lastLoadedIdRef.current = null;
    });
  }, [duplicateId, id, loadClassOfService, location.key, location.pathname, location.search]);

  useEffect(() => {
    if (hasLoadedOptionsRef.current) {
      return;
    }
    hasLoadedOptionsRef.current = true;

    const loadOptions = async () => {
      setIsLoadingOptions(true);
      try {
        const [featureCodes, dialRules, ars] = await Promise.all([
          fetchFeatureCodeCategories(tRef.current),
          fetchDialRuleRestrictions(tRef.current),
          fetchArsProfiles(tRef.current),
        ]);

        setFeatureCodeCategories(featureCodes);
        setDialRuleRestrictions(dialRules);
        setArsProfiles(ars);
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : tRef.current('classOfServices.messages.optionsLoadError');
        showErrorRef.current(message);
      } finally {
        setIsLoadingOptions(false);
      }
    };

    loadOptions();
  }, []);

  return {
    isLoading,
    formData,
    setFormData,
    featureCodeCategories,
    dialRuleRestrictions,
    arsProfiles,
    isLoadingOptions,
  };
}

